import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import * as React from "react";
import { Spinner } from "@/components/ui/spinner";
import { Toaster } from "sonner";
import { AnalyticsProvider } from "@/app/providers/analytics-provider";
import { ThemeProvider } from "@/app/providers/theme-provider";
import AudioProvider from "@/app/providers/audio-provider";
import AppProvider from "@/app/providers/app-provider";
import { BrowserRouter } from "react-router";
import { AppErrorBoundary } from "@/components/errors/app-error-boundary";

type AppProviderProps = {
	children: React.ReactNode;
};

export const Providers = ({ children }: AppProviderProps) => {
	const [queryClient] = React.useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						retry: 1,
						staleTime: 1000 * 60 * 5, // 5 minutes
					},
				},
			}),
	);

	return (
		<React.Suspense
			fallback={
				<div className="flex h-screen w-screen items-center justify-center">
					<Spinner size="xl" />
				</div>
			}
		>
			<AppErrorBoundary>
				<AnalyticsProvider>
					<QueryClientProvider client={queryClient}>
						<BrowserRouter>
							<ThemeProvider>
								<AppProvider>
									<AudioProvider>
										{/* Development tools */}
										{import.meta.env.VITE_ENV === "development" && (
											<ReactQueryDevtools />
										)}
										<Toaster />
										{children}
									</AudioProvider>
								</AppProvider>
							</ThemeProvider>
						</BrowserRouter>
					</QueryClientProvider>
				</AnalyticsProvider>
			</AppErrorBoundary>
		</React.Suspense>
	);
};
